import React, { useState } from "react";
import { Card, Upload, Checkbox, message, Spin } from "antd";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const PdfSplitter = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewPages, setPreviewPages] = useState([]);
  const [allThumbnails, setAllThumbnails] = useState({});
  const pagesPerPage = 30;
  const { t } = useTranslation();

  const beforeUpload = async (file) => {
    if (file.type !== "application/pdf") {
      message.error(t("only_pdf_supported"));
      return false;
    }
    setLoading(true);
    setPdfFile(file);

    const reader = new FileReader();
    reader.onload = async () => {
      const typedArray = new Uint8Array(reader.result);
      const loadedPdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
      setPdfDoc(loadedPdf);
      setTotalPages(loadedPdf.numPages);
      setCurrentPage(1);
      setAllThumbnails({});
      await loadPageThumbnails(loadedPdf, 1);
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const loadPageThumbnails = async (pdf, pageGroup) => {
    setLoading(true);
    const thumbnails = [];
    const start = (pageGroup - 1) * pagesPerPage + 1;
    const end = Math.min(start + pagesPerPage - 1, pdf.numPages);

    const newThumbnails = {};

    for (let pageNum = start; pageNum <= end; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 0.3 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;
      const dataUrl = canvas.toDataURL("image/png");
      thumbnails.push({ pageNum, dataUrl });

      newThumbnails[pageNum] = dataUrl;
    }

    setPages(thumbnails);
    setAllThumbnails((prev) => ({ ...prev, ...newThumbnails }));
    setLoading(false);
  };

  const handlePageChange = async (newPage) => {
    if (!pdfDoc) return;
    setCurrentPage(newPage);
    await loadPageThumbnails(pdfDoc, newPage);
  };

  const handleCheckboxChange = (pageNum) => {
    setSelectedPages((prev) =>
      prev.includes(pageNum)
        ? prev.filter((p) => p !== pageNum)
        : [...prev, pageNum]
    );
  };

  const handleDownloadSelected = async () => {
    if (!pdfFile || selectedPages.length === 0) {
      message.error(t("something_went_wrong"));
      return;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const existingPdfBytes = reader.result;
      const originalPdf = await PDFDocument.load(existingPdfBytes);
      const newPdfDoc = await PDFDocument.create();
      const sortedPages = [...selectedPages].sort((a, b) => a - b);

      for (const pageNum of sortedPages) {
        const [copiedPage] = await newPdfDoc.copyPages(originalPdf, [pageNum - 1]);
        newPdfDoc.addPage(copiedPage);
      }

      const newPdfBytes = await newPdfDoc.save();
      const blob = new Blob([newPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "selected_pages.pdf";
      link.click();
      setLoading(false);
    };
    reader.readAsArrayBuffer(pdfFile);
  };

  const handlePreviewSelected = () => {
    const selectedThumbnails = selectedPages
      .map((pageNum) => ({
        pageNum,
        dataUrl: allThumbnails[pageNum],
      }))
      .filter((p) => p.dataUrl);

    selectedThumbnails.sort((a, b) => a.pageNum - b.pageNum);

    setPreviewPages(selectedThumbnails);
    setPreviewVisible(true);
  };

  const totalPageGroups = Math.ceil(totalPages / pagesPerPage);

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          {!pdfFile && (
            <div className="min-h-[70vh] flex items-center justify-center">
              <Upload beforeUpload={beforeUpload} showUploadList={false} accept=".pdf">
                <StandardButton size="large" type="secondary">
                  {t("upload_pdf_button")}
                </StandardButton>
              </Upload>
            </div>
          )}

          {loading && (
            <div className="flex justify-center items-center min-h-[300px]">
              <Spin size="large" />
            </div>
          )}

          {pages.length > 0 && !loading && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {pages.map(({ pageNum, dataUrl }) => (
                  <div key={pageNum} className="relative border rounded overflow-hidden">
                    <Checkbox
                      className="absolute top-2 left-2 z-10 bg-white bg-opacity-80 rounded"
                      checked={selectedPages.includes(pageNum)}
                      onChange={() => handleCheckboxChange(pageNum)}
                    />
                    <img src={dataUrl} alt={`Page ${pageNum}`} className="w-full" />
                    <div className="text-center py-1 text-xs">{t("page_label")} {pageNum}</div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center items-center gap-4 mt-6">
                <StandardButton
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  type="secondary"
                >
                  {t("previous")}
                </StandardButton>
                <div className="text-sm text-gray-600">
                  {t("page_label")} {currentPage} {t("of_label")} {totalPageGroups}
                </div>
                <StandardButton
                  disabled={currentPage === totalPageGroups}
                  onClick={() => handlePageChange(currentPage + 1)}
                  type="secondary"
                >
                  {t("next")}
                </StandardButton>
              </div>

              <div className="mt-6 flex justify-center gap-4">
                <StandardButton disabled={selectedPages.length === 0} onClick={handlePreviewSelected} type="secondary">
                  {t("preview_button")}
                </StandardButton>
                <StandardButton disabled={selectedPages.length === 0} onClick={handleDownloadSelected} type="secondary">
                  {t("download_button")}
                </StandardButton>
              </div>
            </>
          )}
        </Card>
        <Footer />
      </div>

      {previewVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full overflow-y-auto max-h-[90vh]">
            <h2 className="text-xl font-semibold mb-4">{t("selected_pages")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {previewPages.map(({ pageNum, dataUrl }) => (
                <div key={pageNum} className="text-center">
                  <img src={dataUrl} alt={`Page ${pageNum}`} className="w-full rounded" />
                  <div className="text-sm mt-2">{t("page_label")} {pageNum}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <StandardButton onClick={() => setPreviewVisible(false)} type="secondary">
                {t("close")}
              </StandardButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfSplitter;